<template>
  <div class="plan-view-overlay" @click.self="closeDialog">
    <div class="plan-view-dialog">
      
      <!-- 헤더 -->
      <div class="dialog-header">
        <div class="header-left">
          <img :src="companyInfo.short_img" alt="보험사" />
          <div class="header-info">
            <h2>{{ companyInfo.name || plan.company_etc }}</h2>
            <span>{{ plan.type || getTypeLabel(plan.type_id) }}</span>
          </div>
        </div>
        <button class="close-btn" @click="closeDialog">
          <i class="ph-bold ph-x"></i>
        </button>
      </div>

      <!-- 기본정보 -->
      <div class="plan-basic-info">
        <div class="info-row">
          <div class="info-item">
            <label>납입</label>
            <span>{{ plan.pay }}{{ getPayTypeShort(plan.pay_type) }}</span>
          </div>
          <div class="info-item">
            <label>만기</label>
            <span>{{ plan.end }}{{ getEndTypeShort(plan.end_type) }}</span>
          </div>
          <div class="info-item">
            <label>가입일</label>
            <span>{{ plan.joined_at }}</span>
          </div>
          <div class="info-item highlight">
            <label>보험료</label>
            <span>{{ formatMoney(plan.price) }}원</span>
          </div>
        </div>
      </div>

      <!-- Remark Tags -->
      <div class="plan-remarks-bar" v-if="!loading && remarkTags.length > 0">
        <span 
          v-for="(tag, ti) in remarkTags" :key="ti"
          class="rtag"
          :class="'rtag-' + tag.color"
        >{{ tag.text }}</span>
      </div>

      <!-- 담보 리스트 -->
      <div class="coverage-container" v-if="!loading">
        <template v-for="(chapter, chapterKey) in chapterList" :key="chapterKey">
          <div class="chapter-section" v-if="getChapterCoverages(chapter).length > 0">
            <h3 class="chapter-title">{{ chapter.name }}</h3>
            <ul class="coverage-list">
              <template v-for="child in Object.values(chapter.list)" :key="child.idx">
                <template v-for="cov in getCoveragesByChapter(child.idx)" :key="cov.idx">
                  <li class="coverage-item" :class="{ important: cov.style_important, bold: cov.style_bold }">
                    <span class="cov-name">{{ cov.name }}</span>
                    <span class="cov-value">{{ formatMoney(cov.value) }}<small>만원</small></span>
                  </li>
                </template>
              </template>
            </ul>
          </div>
        </template>

        <div class="empty-state" v-if="coverageList.length === 0">
          <i class="ph ph-shield-slash"></i>
          <p>등록된 담보가 없습니다</p>
        </div>
      </div>

      <!-- 로딩 -->
      <div class="loading-state" v-else>
        <i class="ph ph-spinner"></i>
        <p>불러오는 중...</p>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, reactive, inject, onMounted, computed } from 'vue';

export default {
  name: 'PlanViewDialog',
  props: {
    planIdx: [Number, String],
    planObject: Object,
    closeFunction: Function,
  },
  setup(props) {
    const sql_query = inject('sql_query');
    const getInsuCompanyInfo = inject('getInsuCompanyInfo');

    const loading = ref(true);
    const plan = ref({});
    const coverageList = reactive([]);
    const chapterList = ref({});

    // 보험사 정보
    const companyInfo = computed(() => {
      if (!plan.value.company) return { name: '', short_img: '' };
      return getInsuCompanyInfo(plan.value.company);
    });

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // remarks 파싱 (DB에 직접 쓴 내용)
    // 포맷: "g:유병자 O|r:높은 가격|b:무해지"
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    function parseRemarks(remarks) {
      if (!remarks || !remarks.trim()) return [];
      const colorMap = { 'g': 'green', 'r': 'red', 'b': 'blue', 'y': 'yellow' };
      return remarks.split('|').map(item => {
        const trimmed = item.trim();
        if (!trimmed) return null;
        const match = trimmed.match(/^([grby]):(.+)$/);
        if (match) {
          return { color: colorMap[match[1]], text: match[2].trim() };
        }
        return { color: 'default', text: trimmed };
      }).filter(Boolean);
    }

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // 담보 기반 자동 태그 생성 기준
    // 여기만 수정하면 자동 태그가 바뀜
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // 자동 태그 기준 설정
    // 여기만 수정하면 됨!
    // 
    // type 종류:
    //   'count'         → 전체 담보 개수
    //   'chapter_count' → 특정 챕터의 담보 개수
    //   'chapter_max'   → 특정 챕터의 최고 금액 (만원)
    //   'chapter_sum'   → 특정 챕터의 금액 합계 (만원)
    //   'total_max'     → 전체 담보 중 최고 금액 (만원)
    //   'total_avg'     → 전체 담보 평균 금액 (만원)
    //   'price'         → 보험료 (원)
    //   'rate'          → 평점
    //   'renewable'     → 갱신 여부 (0=비갱신, 1=갱신)
    //
    // match: 챕터 이름에 포함된 키워드 (chapter_ 타입에만 사용)
    // threshold: 기준값
    // below: 기준보다 낮을 때 태그
    // above: 기준보다 높을 때 태그
    // eq: 정확히 일치할 때 태그 (renewable 등)
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    const autoCriteria = [
      // ── 담보 개수 ──
      { type: 'count',         threshold: 15,    below: 'y:보장항목 적음',   above: 'g:보장항목 많음' },

      // ── 챕터별 최고 금액 (만원) ──
      { type: 'chapter_max',   match: '암',      threshold: 3000,  below: 'r:암보장 부족',    above: 'g:암보장 좋음' },
      { type: 'chapter_max',   match: '뇌',      threshold: 3000,  below: 'r:뇌보장 부족',    above: 'g:뇌보장 좋음' },
      { type: 'chapter_max',   match: '심장',    threshold: 3000,  below: 'r:심장보장 부족',   above: 'g:심장보장 좋음' },
      { type: 'chapter_max',   match: '수술',    threshold: 500,   below: 'r:수술비 부족',    above: 'g:수술비 좋음' },
      { type: 'chapter_max',   match: '입원',    threshold: 10,    below: 'r:입원비 부족',    above: 'g:입원비 좋음' },
      { type: 'chapter_max',   match: '사망',    threshold: 10000, below: 'y:사망보장 낮음',   above: 'g:사망보장 높음' },
      { type: 'chapter_max',   match: '후유장해', threshold: 10000, below: 'y:후유장해 낮음',   above: 'g:후유장해 높음' },
      { type: 'chapter_max',   match: '진단',    threshold: 2000,  below: 'y:진단금 낮음',    above: 'g:진단금 좋음' },

      // ── 챕터별 담보 개수 ──
      { type: 'chapter_count', match: '실손',    threshold: 1,     below: 'r:실손 미가입',    above: 'b:실손 가입' },

      // ── 전체 금액 ──
      { type: 'total_avg',     threshold: 1500,  below: 'y:평균 보장금 낮음', above: 'g:평균 보장금 높음' },

      // ── 보험료 (원) ──
      { type: 'price',         threshold: 80000, below: null,               above: 'y:높은 보험료' },
      { type: 'price',         threshold: 30000, below: 'g:저렴한 보험료',   above: null },

      // ── 평점 ──
      { type: 'rate',          threshold: 85,    below: null,               above: 'g:높은 평점' },
      { type: 'rate',          threshold: 60,    below: 'r:낮은 평점',      above: null },

      // ── 갱신 여부 ──
      { type: 'renewable',     eq: 0, tag: 'g:비갱신형' },
      { type: 'renewable',     eq: 1, tag: 'y:갱신형' },
    ];

    /**
     * 챕터 이름으로 담보 찾기 (chapterList에서 이름에 keyword 포함된 챕터의 담보들)
     */
    function getCoveragesByKeyword(keyword) {
      const matchedChapterIdxs = [];
      for (const root of Object.values(chapterList.value)) {
        // 루트 챕터 이름 매칭
        if (root.name.includes(keyword)) {
          Object.values(root.list).forEach(child => matchedChapterIdxs.push(child.idx));
        }
        // 자식 챕터 이름 매칭
        for (const child of Object.values(root.list)) {
          if (child.name.includes(keyword)) matchedChapterIdxs.push(child.idx);
        }
      }
      return coverageList.filter(c => 
        matchedChapterIdxs.includes(c.chapter_idx) && !c.is_union && !c.low_show
      );
    }

    /**
     * 통합 자동 태그 생성 엔진
     */
    function generateAutoRemarks() {
      const tags = [];
      const visibleCovs = coverageList.filter(c => !c.is_union && !c.low_show);
      const values = visibleCovs.map(c => Number(c.value) || 0);

      for (const rule of autoCriteria) {
        let val = null;

        // eq 타입 (renewable 등 정확히 일치)
        if (rule.eq !== undefined) {
          const fieldVal = plan.value[rule.type];
          if (fieldVal !== undefined && String(fieldVal) === String(rule.eq)) {
            tags.push(...parseRemarks(rule.tag));
          }
          continue;
        }

        // 값 계산
        switch (rule.type) {
          case 'count':
            val = visibleCovs.length;
            break;
          case 'chapter_count': {
            const matched = getCoveragesByKeyword(rule.match);
            val = matched.length;
            break;
          }
          case 'chapter_max': {
            const matched = getCoveragesByKeyword(rule.match);
            if (matched.length === 0) continue;
            val = Math.max(...matched.map(c => Number(c.value) || 0));
            break;
          }
          case 'chapter_sum': {
            const matched = getCoveragesByKeyword(rule.match);
            if (matched.length === 0) continue;
            val = matched.reduce((sum, c) => sum + (Number(c.value) || 0), 0);
            break;
          }
          case 'total_max':
            if (values.length === 0) continue;
            val = Math.max(...values);
            break;
          case 'total_avg':
            if (values.length === 0) continue;
            val = values.reduce((a, b) => a + b, 0) / values.length;
            break;
          case 'price':
            val = Number(plan.value.price) || null;
            break;
          case 'rate':
            val = Number(plan.value.rate) || null;
            break;
          default:
            continue;
        }

        if (val === null) continue;

        // threshold 비교
        if (val < rule.threshold && rule.below) {
          tags.push(...parseRemarks(rule.below));
        } else if (val >= rule.threshold && rule.above) {
          tags.push(...parseRemarks(rule.above));
        }
      }

      return tags;
    }

    /**
     * 최종 remark 태그
     * 1순위: DB remarks (직접 입력)
     * 2순위: 담보 기반 자동 생성 + 플랜 필드 기반
     */
    const remarkTags = computed(() => {
      const manual = parseRemarks(plan.value.remarks);
      if (manual.length > 0) return manual;
      return generateAutoRemarks();
    });

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    // 플랜 로드
    async function fetchPlan() {
      const idx = props.planIdx || props.planObject?.idx;
      if (!idx) return;

      const sql = `SELECT * FROM IS_plan WHERE idx = '${idx}' LIMIT 1`;
      const result = await sql_query(sql);
      
      if (result && result[0]) {
        plan.value = {
          ...result[0],
          joined_at: (result[0].joined_at || '').slice(0, 10),
        };
      }
    }

    // 챕터 로드
    async function fetchChapter() {
      const rootSql = `SELECT * FROM IS_chapter WHERE (parent IS NULL OR parent = 0) AND status = 1 ORDER BY no ASC`;
      const rootChapters = await sql_query(rootSql);

      for (const chapter of rootChapters) {
        chapterList.value[chapter.en_name] = {
          idx: chapter.idx,
          no: chapter.no,
          name: chapter.name,
          list: {}
        };
      }

      const childSql = `SELECT * FROM IS_chapter WHERE parent IS NOT NULL AND parent != 0 AND status = 1 ORDER BY no ASC`;
      const childChapters = await sql_query(childSql);

      for (const chapter of childChapters) {
        const parentObj = rootChapters.find(r => r.idx === chapter.parent);
        if (!parentObj || !chapterList.value[parentObj.en_name]) continue;

        chapterList.value[parentObj.en_name].list[chapter.en_name] = {
          name: chapter.name,
          idx: chapter.idx,
          no: chapter.no,
        };
      }
    }

    // 담보 로드
    async function fetchCoverages() {
      const idx = props.planIdx || props.planObject?.idx;
      if (!idx) return;

      const originals = await sql_query(`SELECT * FROM IS_ex_coverage`);
      const originalMap = new Map(originals.map(o => [o.idx, o]));

      const sql = `SELECT * FROM IS_coverage WHERE plan_id = '${idx}' ORDER BY idx DESC`;
      const coverages = await sql_query(sql);

      for (const coverage of coverages) {
        const original = originalMap.get(coverage.original);
        
        if (!coverage.visible) continue;

        coverageList.push({
          idx: coverage.idx,
          name: coverage.nick || original?.name || '',
          no: original?.no || 9999,
          short_name: coverage.short_name || original?.short_name || '',
          chapter_idx: coverage.chapter_id || original?.chapter_id,
          value: coverage.value,
          style_bold: original?.style_bold || 0,
          style_important: original?.style_important || 0,
          is_union: coverage?.is_union || 0,
          low_show: coverage?.low_show || 0,
        });
      }
    }

    // 챕터별 담보 가져오기
    function getCoveragesByChapter(chapterIdx) {
      return coverageList
        .filter(c => c.chapter_idx == chapterIdx && !c.is_union && !c.low_show)
        .sort((a, b) => a.no - b.no);
    }

    // 챕터 내 전체 담보 (표시 여부 체크용)
    function getChapterCoverages(chapter) {
      let all = [];
      Object.values(chapter.list).forEach(child => {
        all.push(...getCoveragesByChapter(child.idx));
      });
      return all;
    }

    // 유틸리티
    function formatMoney(val) {
      if (!val) return '0';
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    function getPayTypeShort(code) {
      return { 0: "년납", 1: "세납", 2: "일시납", 3: "" }[code] || "";
    }

    function getEndTypeShort(code) {
      return { 0: "세만기", 1: "년만기", 2: "종신", 3: "" }[code] || "";
    }

    function getTypeLabel(code) {
      const map = {
        0: "직접입력", 1: "종합보장", 2: "건강/상해", 3: "단독실손",
        4: "종신/정기", 5: "연금/저축", 6: "회사/법인", 7: "실버/상속",
        8: "자녀/태아", 9: "운전자", 10: "치아보험", 11: "골프/레저", 12: "주택/건물"
      };
      return map[code] || "기타";
    }

    function closeDialog() {
      if (props.closeFunction) {
        props.closeFunction(false);
      }
    }

    // 초기화
    onMounted(async () => {
      loading.value = true;
      await fetchChapter();
      await fetchPlan();
      await fetchCoverages();
      loading.value = false;
    });

    return {
      loading,
      plan,
      coverageList,
      chapterList,
      companyInfo,
      remarkTags,
      getCoveragesByChapter,
      getChapterCoverages,
      formatMoney,
      getPayTypeShort,
      getEndTypeShort,
      getTypeLabel,
      closeDialog,
    };
  },
}
</script>

<style scoped>
.plan-view-overlay {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.plan-view-dialog {
  width: 90%;
  max-width: 560px;
  max-height: 85vh;
  background: var(--color-background);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

/* 헤더 */
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-surface);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-left img {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
}

.header-info h2 {
  font-size: var(--font-size-lg);
  font-weight: 700;
  margin: 0 0 4px 0;
  color: var(--color-text-primary);
}

.header-info span {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.close-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: var(--color-background);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  font-size: 18px;
  transition: all 0.15s;
}

.close-btn:hover {
  background: var(--color-border);
  color: var(--color-text-primary);
}

/* 기본정보 */
.plan-basic-info {
  padding: 16px 20px;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
}

.info-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.info-row + .info-row {
  margin-top: 12px;
}

.info-item {
  flex: 1;
  min-width: 80px;
}

.info-item.full {
  flex: 100%;
}

.info-item.highlight span {
  color: var(--color-primary);
  font-weight: 700;
}

.info-item label {
  display: block;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  margin-bottom: 4px;
}

.info-item span {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--color-text-primary);
}

/* ===== Remark Tags Bar ===== */
.plan-remarks-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-surface);
}

.rtag {
  display: inline-block;
  font-size: var(--font-size-xs);
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 100px;
  line-height: 1.6;
  white-space: nowrap;
}

.rtag-green {
  background: rgba(34, 197, 94, 0.12);
  color: #22C55E;
}

.rtag-red {
  background: rgba(239, 68, 68, 0.12);
  color: #EF4444;
}

.rtag-blue {
  background: rgba(59, 130, 246, 0.12);
  color: #3B82F6;
}

.rtag-yellow {
  background: rgba(245, 158, 11, 0.12);
  color: #F59E0B;
}

.rtag-default {
  background: rgba(128, 128, 128, 0.12);
  color: var(--color-text-secondary);
}

/* 담보 컨테이너 */
.coverage-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.chapter-section {
  margin-bottom: 20px;
}

.chapter-section:last-child {
  margin-bottom: 0;
}

.chapter-title {
  font-size: var(--font-size-sm);
  font-weight: 700;
  color: var(--color-text-secondary);
  margin: 0 0 8px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-border);
}

.coverage-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.coverage-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: var(--color-surface);
  border-radius: 8px;
  margin-bottom: 6px;
}

.coverage-item:last-child {
  margin-bottom: 0;
}

.coverage-item.bold {
  font-weight: 700;
}

.coverage-item.important {
  opacity: 0.6;
}

.cov-name {
  font-size: var(--font-size-md);
  color: var(--color-text-primary);
  flex: 1;
  margin-right: 12px;
}

.cov-value {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--color-text-primary);
  white-space: nowrap;
}

.cov-value small {
  font-size: var(--font-size-xs);
  font-weight: 400;
  color: var(--color-text-secondary);
  margin-left: 2px;
}

/* 빈 상태 / 로딩 */
.empty-state,
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: var(--color-text-secondary);
}

.empty-state i,
.loading-state i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.loading-state i {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.empty-state p,
.loading-state p {
  font-size: var(--font-size-md);
  margin: 0;
}

/* 스크롤바 */
.coverage-container::-webkit-scrollbar {
  width: 6px;
}

.coverage-container::-webkit-scrollbar-track {
  background: transparent;
}

.coverage-container::-webkit-scrollbar-thumb {
  background: var(--color-border);
  border-radius: 3px;
}

/* 모바일 */
@media (max-width: 480px) {
  .plan-view-dialog {
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .info-item {
    min-width: 70px;
  }

  .plan-remarks-bar {
    padding: 10px 16px;
    gap: 5px;
  }
}
</style>